import { Box, Grid, Typography, debounce, makeStyles } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { AutocompleteInputChangeReason, createFilterOptions } from "@material-ui/lab";
import dayjs from "dayjs";
import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";

import { HowMaBuilding, HowMaRoom, IdName, PropertyBuilder, PropertyStructureEnum } from "@/api/generated";
import { GetCities, GetPrefectures, GetRailways, GetStations, GetTowns } from "@/api/getAddress";
import { CustomButton } from "@/components/Button";
import { RegistrationActions } from "@/components/property_registration/RegistrationActions";
import { RegistrationSection } from "@/components/property_registration/RegistrationActions";
import { PropertyRegistrationMode } from "@/components/property_registration/RegistrationContext";
import { RegistrationAutocomplete } from "@/components/property_registration/RegistrationInputs";
import {
  RegistrationAutoCompleteForm as CustomAutoCompleteForm,
  CustomForm,
} from "@/components/property_registration/RegistrationInputs";
import { CustomSelector } from "@/components/property_registration/RegistrationInputs";
import { Toast } from "@/components/Toast";
import { Layout } from "@/constants/addNewProperty";
import {
  convertAreaNameToCity,
  convertAreaNameToPrefecture,
  convertAreaNameToTown,
  convertFloorPlanToLayout,
} from "@/logics/howma/howMaBuildingSearchApiLogic";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import {
  ConsiderationPropertyAiInputUsedState,
  ConsiderationPropertyRegistrationState,
  PropertyManagementDetailSnackbarState,
} from "@/view_models/atoms";

// ========================================
// Styles
// ========================================
const useStyles = makeStyles(() => ({
  content: {
    width: 640,
    margin: "32px auto 32px auto",
    [theme.breakpoints.down("sm")]: {
      width: 342,
      margin: "16px auto 0px auto",
    },
  },
  aiSection: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 8,
    padding: "24px",
    marginBottom: "24px",
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.down("sm")]: {
      padding: "16px",
    },
  },
  aiTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1.5),
  },
  aiDescription: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  "@keyframes registrationSpin": { to: { transform: "rotate(360deg)" } },
  spinner: { display: "block", animation: "$registrationSpin 1.4s linear infinite" },
  loadingOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.semantic["surface/overlay"],

    zIndex: 1600,
  },
  loadingCard: {
    width: 294,
    maxWidth: "calc(100vw - 48px)",
    borderRadius: 16,
    padding: "32px 24px",
    backgroundColor: theme.palette.semantic["surface/card"],
    gap: 16,
    [theme.breakpoints.up("md")]: { width: 440, padding: 40 },
  },
  aiLoadingTitle: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.5,
    marginBottom: theme.spacing(0.5),
    textAlign: "center",
  },
  aiLoadingDescription: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.5,
    textAlign: "center",
  },
}));

// ========================================
// Types
// ========================================
interface AddNewConsiderationPropertyBasicPageProps {
  registrationMode?: PropertyRegistrationMode;
  onAiReview?: () => void;
  onNext: () => void;
  validate: () => Promise<boolean>;
}

// ========================================
// Component
// ========================================
const AddNewConsiderationPropertyBasicPage: React.FC<AddNewConsiderationPropertyBasicPageProps> = ({
  onNext,
  validate,
  registrationMode,
  onAiReview,
}) => {
  const classes = useStyles();
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext();
  const fp = React.useMemo(() => PropallyApiFactory(), []);

  // ========================================
  // Recoil State
  // ========================================
  const [formData, setFormData] = useRecoilState(ConsiderationPropertyRegistrationState);
  const setDetailSnackBarState = useSetRecoilState(PropertyManagementDetailSnackbarState);
  const setAiInputUsedState = useSetRecoilState(ConsiderationPropertyAiInputUsedState);

  // ========================================
  // Local State - Master Data
  // ========================================
  const [prefectures, setPrefectures] = React.useState<Array<string>>([""]);
  const [cities, setCities] = React.useState<Array<string>>([formData.city ? formData.city : ""]);
  const [towns, setTowns] = React.useState<Array<string>>([""]);
  const [railways, setRailways] = React.useState<Array<IdName>>([]);
  const [stations, setStations] = React.useState<Array<string>>([]);

  // ========================================
  // Local State - Building Search
  // ========================================
  const [buildingOptions, setBuildingOptions] = React.useState<Array<HowMaBuilding>>([]);
  const [roomOptions, setRoomOptions] = React.useState<Array<HowMaRoom>>([]);
  const [selectedHowMaBuilding, setSelectedHowMaBuilding] = React.useState<HowMaBuilding>(null);
  const [selectedHowMaRoom, setSelectedHowMaRoom] = React.useState<HowMaRoom>(null);
  const [isBuildingsSearching, setIsBuildingsSearching] = React.useState<boolean>(false);

  // ========================================
  // Local State - UI
  // ========================================
  const [isAiCreating, setIsAiCreating] = React.useState(false);
  const [serverError, setSeverError] = React.useState<string>("");
  const [successMessage, setSuccessMessage] = React.useState<string>("");

  // イベントハンドラから始めた非同期処理でも、画面を離れた後は状態を更新しない。
  const isAiCreatingRef = React.useRef(false);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ========================================
  // Other
  // ========================================
  const defaultFilterOptions = createFilterOptions();
  const watchValues = useWatch({
    name: ["prefecture", "city", "town", "railway", "property_type"],
    control: control,
  });

  // ========================================
  // Constants
  // ========================================
  const currentYear = new Date().getFullYear();
  const years = Array.from(new Array(90), (val, index) => currentYear - 70 + index);
  const builtAtYearOptions = years.map((year) => ({ value: year.toString(), label: year.toString() }));

  // ========================================
  // Effects - Initial Load
  // ========================================
  React.useEffect(() => {
    if (formData.name) {
      setValue("name", formData.name);
      setValue("prefecture", formData.prefecture);
      setValue("city", formData.city);
      setValue("town", formData.town);
      setValue("railway", formData.railway);
      setValue("station", formData.station);
      setValue("built_at", formData.built_at);
      setValue("room_number", formData.room_number);
      setValue("floor", formData.floor);
      setValue("walk", formData.walk);
      setValue("occupied_area_m2", formData.occupied_area_m2);
      setValue("layout", formData.layout);
    }
  }, []);

  React.useEffect(() => {
    let isMounted = true;
    (async () => {
      const [prefecturesResult, railwaysResult] = await Promise.all([GetPrefectures(), GetRailways()]);

      if (isMounted) {
        if (!prefecturesResult.success) {
          setSeverError(prefecturesResult.error);
        } else {
          setPrefectures(prefecturesResult.prefectures);
        }

        if (!railwaysResult.success) {
          setSeverError(railwaysResult.error);
        } else {
          setRailways(railwaysResult.railways);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  // ========================================
  // Effects - Auto-fill from Selected Building/Room
  // ========================================
  React.useEffect(() => {
    if (!selectedHowMaBuilding) return;
    setValue("name", selectedHowMaBuilding.name);
    setValue("prefecture", convertAreaNameToPrefecture(selectedHowMaBuilding.area.name, prefectures));
    setValue("railway", railways.find((v) => v.id === selectedHowMaBuilding.station?.line_cd)?.name ?? "");
    setValue("station", selectedHowMaBuilding.station?.name ?? "");
    setValue("built_at", selectedHowMaBuilding.built_at_year.toString());
  }, [selectedHowMaBuilding, prefectures, railways, setValue]);

  React.useEffect(() => {
    if (!selectedHowMaRoom) return;
    setValue("room_number", selectedHowMaRoom.name);
    setValue("floor", selectedHowMaRoom.floor_number.toString());
    setValue("occupied_area_m2", selectedHowMaRoom.m2.toString());
    setValue("layout", convertFloorPlanToLayout(selectedHowMaRoom.floor_plan));
  }, [selectedHowMaRoom, setValue]);

  // ========================================
  // Effects - Load Master Data on Selection
  // ========================================
  React.useEffect(() => {
    if (!watchValues.prefecture || watchValues.prefecture === "") {
      setValue("city", "");
      setValue("town", "");
      setCities([""]);
      setTowns([""]);
      return;
    }

    let isMounted = true;
    (async () => {
      const r = await GetCities(watchValues.prefecture);
      if (isMounted) {
        if (!r.success) {
          setSeverError(r.error);
        } else {
          setCities(r.cities);
          if (selectedHowMaBuilding) {
            setValue("city", convertAreaNameToCity(selectedHowMaBuilding.area.name, r.cities));
          }
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [watchValues.prefecture, selectedHowMaBuilding, setValue]);

  React.useEffect(() => {
    if (!watchValues.city || watchValues.city === "") return;

    let isMounted = true;
    (async () => {
      const r = await GetTowns(watchValues.prefecture, watchValues.city);
      if (isMounted) {
        if (!r.success) {
          setSeverError(r.error);
        } else {
          setTowns(r.towns);
          if (selectedHowMaBuilding) {
            setValue("town", convertAreaNameToTown(selectedHowMaBuilding.area.name, r.towns));
          }
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [watchValues.city, watchValues.prefecture, selectedHowMaBuilding, setValue]);

  React.useEffect(() => {
    if (!watchValues.railway || watchValues.railway === "") return;

    let isMounted = true;
    (async () => {
      const r = await GetStations(watchValues.railway);
      if (isMounted) {
        if (!r.success) {
          setSeverError(r.error);
        } else {
          setStations(r.stations);
          if (selectedHowMaBuilding) {
            setValue("station", selectedHowMaBuilding.station?.name);
          }
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [watchValues.railway, selectedHowMaBuilding, setValue]);

  // ========================================
  // Helper Functions
  // ========================================
  const toStringOrUndefined = (value?: string | number | null) => {
    if (value === null || value === undefined) return undefined;
    const str = typeof value === "number" ? value.toString() : value;
    return str.trim() === "" ? undefined : str;
  };

  const toYearMonthAndDay = (value?: string | null) => {
    if (!value) return { yearMonth: undefined, day: undefined };
    const parsed = dayjs(value);
    if (!parsed.isValid()) return { yearMonth: undefined, day: undefined };
    return { yearMonth: parsed.format("YYYY-MM"), day: parsed.format("D") };
  };

  const mapDraftToFormSchema = (draft: PropertyBuilder) => {
    const boughtAt = toYearMonthAndDay(draft.bought_at);
    const paymentStartsAt = toYearMonthAndDay(draft.payment_starts_at);
    const leaseContract =
      draft.lease_contract ?? draft.lease_contract_current ?? draft.lease_contract_latest ?? draft.lease_contracts?.[0];
    const leaseContractStartsAt = toYearMonthAndDay(leaseContract?.starts_at);
    const leaseContractEndsAt = toYearMonthAndDay(leaseContract?.ends_at);
    const cashflow =
      draft.cashflow_result_of_the_first_month ??
      draft.monthly_cashflow_result_latest ??
      draft.monthly_cashflow_results?.[0];

    const builtAtYear =
      draft.built_at && dayjs(draft.built_at).isValid() ? dayjs(draft.built_at).format("YYYY") : undefined;
    const block = toStringOrUndefined(draft.block);
    const town = block ?? toStringOrUndefined(draft.town);

    return {
      // Step1: 基本情報
      name: toStringOrUndefined(draft.name),
      prefecture: toStringOrUndefined(draft.prefecture),
      city: toStringOrUndefined(draft.city),
      town,
      block,
      railway: toStringOrUndefined(draft.railway),
      station: toStringOrUndefined(draft.station),
      built_at: builtAtYear,
      structure: toStringOrUndefined(draft.structure),
      room_number: toStringOrUndefined(draft.unit_detail?.room_number),
      floor: toStringOrUndefined(draft.unit_detail?.floor),
      occupied_area_m2: toStringOrUndefined(draft.unit_detail?.occupied_area_m2),
      layout: toStringOrUndefined(draft.unit_detail?.layout),

      // Step2: 価格・ローン
      bought_at: boughtAt.yearMonth,
      bought_at_day: boughtAt.day,
      total_price_of_property: toStringOrUndefined(draft.total_price_of_property),
      priceof_tax: toStringOrUndefined(draft.priceof_tax as unknown as number | string | null),
      priceof_building: toStringOrUndefined(draft.priceof_building),
      priceof_facilities: toStringOrUndefined(draft.priceof_facilities),
      priceof_land: toStringOrUndefined(draft.priceof_land),
      loan_amount: toStringOrUndefined(draft.loan_amount),
      loan_interest_rate_percentage: toStringOrUndefined(cashflow?.loan_interest_rate_percentage),
      payment_starts_at: paymentStartsAt.yearMonth,
      payment_period: toStringOrUndefined(draft.payment_period),
      property_acquisition_tax: toStringOrUndefined(draft.property_acquisition_tax),
      property_acquisition_tax_payment_month: toStringOrUndefined(draft.property_acquisition_tax_payment_month),
      misc_expences_at_bought: toStringOrUndefined(draft.misc_expences_at_bought),
      asking_price: toStringOrUndefined(draft.asking_price),

      // Step3: 月間収支予定
      monthly_fee:
        toStringOrUndefined(leaseContract?.monthly_fee) ??
        toStringOrUndefined(draft.building_room?.monthly_fee) ??
        toStringOrUndefined(cashflow?.rent_income),
      monthly_management_fee:
        toStringOrUndefined(leaseContract?.monthly_management_fee) ??
        toStringOrUndefined(cashflow?.management_fee_income),
      type_of_the_income_of_the_beginning_of_contract: toStringOrUndefined(
        leaseContract?.type_of_the_income_of_the_beginning_of_contract
      ),
      security_deposit: toStringOrUndefined(leaseContract?.security_deposit),
      key_money: toStringOrUndefined(leaseContract?.key_money),
      outsourcing_fee_rate_of_new_lease_contract: toStringOrUndefined(
        leaseContract?.outsourcing_fee_rate_of_new_lease_contract
      ),
      outsourcing_fee_rate_of_updating_lease_contract: toStringOrUndefined(
        leaseContract?.outsourcing_fee_rate_of_updating_lease_contract
      ),
      starts_at: leaseContractStartsAt.yearMonth,
      starts_at_day: leaseContractStartsAt.day,
      ends_at: leaseContractEndsAt.yearMonth,
      ends_at_day: leaseContractEndsAt.day,
      building_management_cost: toStringOrUndefined(cashflow?.building_management_cost),
      repair_fund_cost: toStringOrUndefined(cashflow?.repair_fund_cost),
      management_cost: toStringOrUndefined(cashflow?.management_cost),
      misc_cost: toStringOrUndefined(cashflow?.misc_cost),
    } as const;
  };

  // ========================================
  // Handlers
  // ========================================
  const handleNext = async () => {
    const isValid = await validate();
    if (isValid) {
      sendEventToAppMeasurementTool(AppEventTokenEnum.ConsiderationPropertyBasicInfoCompleted);
      onNext();
    }
  };

  const handleClose = React.useCallback(() => {
    setSeverError("");
  }, []);

  const handleCloseSuccess = React.useCallback(() => {
    setSuccessMessage("");
  }, []);

  const applyDraftToForm = (draft: PropertyBuilder) => {
    const mapped = mapDraftToFormSchema(draft);
    const step1Keys = new Set([
      "name",
      "prefecture",
      "city",
      "town",
      "block",
      "railway",
      "station",
      "walk",
      "built_at",
      "structure",
      "room_number",
      "floor",
      "occupied_area_m2",
      "layout",
    ]);
    const doNotApplyAiDraftKeys = new Set([
      // 購入予定年月日
      "bought_at",
      "bought_at_day",
      // 初回返済月
      "payment_starts_at",
      // 賃貸借契約の開始月/終了月
      "starts_at",
      "starts_at_day",
      "ends_at",
      "ends_at_day",
    ]);
    const mappedForStep2AndAfter = Object.fromEntries(
      Object.entries(mapped).filter(
        ([key, value]) => value !== undefined && !step1Keys.has(key) && !doNotApplyAiDraftKeys.has(key)
      )
    );

    // Recoil へ反映
    setFormData((prev) => ({
      ...prev,
      ...mappedForStep2AndAfter,
    }));

    // react-hook-form へも反映（step2以降のみ）
    const setFormValue = setValue as unknown as (name: string, value: unknown) => void;
    Object.entries(mappedForStep2AndAfter).forEach(([key, value]) => {
      if (value === undefined) return;
      setFormValue(key, value);
    });

    setAiInputUsedState(true);
  };

  const handleAiCreate = async () => {
    if (isAiCreatingRef.current) return;
    isAiCreatingRef.current = true;
    const isValid = await validate();
    if (!isMountedRef.current) {
      isAiCreatingRef.current = false;
      return;
    }
    if (!isValid) {
      isAiCreatingRef.current = false;
      setSeverError("必須項目を入力してください。");
      return;
    }

    const values = getValues();
    const propertyName = String(values?.name || "").trim();
    if (!propertyName) {
      isAiCreatingRef.current = false;
      setSeverError("マンション・アパート名を入力してください。");
      return;
    }

    sendEventToAppMeasurementTool(AppEventTokenEnum.ConsiderationPropertyAiInputStart);
    setIsAiCreating(true);

    try {
      const toNumberOrUndefined = (v: unknown) => {
        if (v === null || v === undefined) return undefined;
        const n = typeof v === "number" ? v : Number(String(v));
        return Number.isFinite(n) ? n : undefined;
      };
      const toIntOrUndefined = (v: unknown) => {
        const n = toNumberOrUndefined(v);
        return n === undefined ? undefined : Math.trunc(n);
      };

      const res = await fp.v1PropertiesAiDraftConsiderationPost(
        {
          property_name: propertyName,
          prefecture: toStringOrUndefined(values?.prefecture),
          city: toStringOrUndefined(values?.city),
          town: toStringOrUndefined(values?.town),
          railway: toStringOrUndefined(values?.railway),
          station: toStringOrUndefined(values?.station),
          walk: toIntOrUndefined(values?.walk),
          built_at: toStringOrUndefined(values?.built_at),
          occupied_area_m2: toNumberOrUndefined(values?.occupied_area_m2),
          layout: toStringOrUndefined(values?.layout),
          structure: toStringOrUndefined(values?.structure),
          room_number: toStringOrUndefined(values?.room_number),
          floor: toIntOrUndefined(values?.floor),
        },
        { withCredentials: true }
      );
      if (!isMountedRef.current) return;
      applyDraftToForm(res.data);
      sendEventToAppMeasurementTool(AppEventTokenEnum.ConsiderationPropertyAiInputSuccess);
      if (onAiReview) onAiReview();
      else onNext();
    } catch (error: unknown) {
      sendEventToAppMeasurementTool(AppEventTokenEnum.ConsiderationPropertyAiInputFailed);
      if (!isMountedRef.current) return;
      const errorCode = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;

      if (errorCode === "GEMINI_API_ERROR") {
        setDetailSnackBarState({
          isOpen: true,
          message: "AIが混み合っています。時間をおいて再度お試しください。",
          status: "error",
        });
        return;
      }

      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "AIおまかせ入力が失敗しました。時間をおいて再度お試しください。";
      setSeverError(message);
    } finally {
      isAiCreatingRef.current = false;
      if (isMountedRef.current) setIsAiCreating(false);
    }
  };

  const buildingsSearch = (searchBuildingName: string) => {
    setValue("name", searchBuildingName);
    setBuildingOptions([]);
    setRoomOptions([]);

    if (searchBuildingName.length < 2) return;

    setIsBuildingsSearching(true);
    debouncedFetchBuildingOptions(searchBuildingName);
  };

  const fetchBuildingOptions = React.useCallback(
    (searchBuildingName: string) => {
      fp.v1HowmaBuildingsPost({ text: searchBuildingName }, { withCredentials: true })
        .then((res) => {
          if (!isMountedRef.current) return;
          setBuildingOptions(res.data.buildings);
          setIsBuildingsSearching(false);
        })
        .catch(() => {
          if (!isMountedRef.current) return;
          setSeverError("物件検索に失敗しました。再度お試し頂くか、手動で入力してください。");
          setIsBuildingsSearching(false);
        });
    },
    [fp]
  );

  const debouncedFetchBuildingOptions = React.useMemo(() => {
    return debounce((name: string) => fetchBuildingOptions(name), 1000);
  }, [fetchBuildingOptions]);

  React.useEffect(() => {
    return () => {
      // @material-ui/core/debounce は clear を持つ
      (debouncedFetchBuildingOptions as any).clear?.();
    };
  }, [debouncedFetchBuildingOptions]);

  const fetchRoomOptions = React.useCallback(
    (buildingId: number) => {
      fp.v1HowmaBuildingsBuildingIdGet(buildingId, { withCredentials: true })
        .then((res) => {
          if (!isMountedRef.current) return;
          setRoomOptions(res.data.rooms);
        })
        .catch(() => {
          if (!isMountedRef.current) return;
          setSeverError("物件検索に失敗しました。再度お試し頂くか、手動で入力してください。");
        });
    },
    [fp]
  );

  // ========================================
  // Render
  // ========================================
  return (
    <>
      <Box>
        <Box className={classes.content} data-registration-content>
          {/* 検索中表示 */}
          {isBuildingsSearching && (
            <Box>
              <Typography variant="subtitle2" color="inherit">
                マンション検索中...
                <CircularProgress style={{ marginLeft: "4px" }} size={14} />
              </Typography>
            </Box>
          )}

          {/* Step1: /add_new_property と同等の入力（管理形態・売却状況・写真は除外） */}
          <>
            {/* マンション・アパート名（Autocomplete機能付き） */}
            <RegistrationSection title="建物と部屋" />
            <Controller
              name="name"
              control={control}
              defaultValue={formData?.name || ""}
              render={({ ...field }) => (
                <RegistrationAutocomplete
                  registrationLabel="マンション・アパート名"
                  registrationOnBlur={field.onBlur}
                  registrationError={errors.name?.message}
                  registrationRequired={true}
                  size="small"
                  freeSolo
                  disableClearable
                  options={buildingOptions}
                  value={field.value}
                  getOptionLabel={(option: HowMaBuilding | string) => {
                    return typeof option === "string" ? option : `${option.name}`;
                  }}
                  renderOption={(option: HowMaBuilding) => (
                    <Box>
                      <Typography variant="body2" component="div">
                        {option.name}
                      </Typography>
                      <Typography variant="caption">({option.area.name})</Typography>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <CustomAutoCompleteForm
                      params={params}
                      label="マンション・アパート名"
                      helperText="マンション・アパート名から物件情報を自動入力する"
                      required
                      ref={field.ref}
                      error={!!errors.name}
                      errorText={errors.name?.message}
                    />
                  )}
                  onChange={(e, selected: HowMaBuilding) => {
                    if (typeof selected === "string") return;
                    setSelectedHowMaBuilding(selected);
                    fetchRoomOptions(selected.id);
                  }}
                  onInputChange={(e: React.ChangeEvent, inputed: string, reason: AutocompleteInputChangeReason) => {
                    if (reason !== "input") return;
                    buildingsSearch(inputed);
                  }}
                  onFocus={() => {}}
                />
              )}
            />

            {/* 部屋選択（候補がある場合のみ） */}
            {roomOptions.length > 0 && (
              <RegistrationAutocomplete
                registrationLabel="部屋"
                registrationError={errors.room_number?.message}
                registrationRequired={true}
                id="room"
                options={roomOptions}
                value={selectedHowMaRoom}
                size="small"
                getOptionLabel={(option: HowMaRoom) => (option ? option.name : "")}
                renderOption={(option: HowMaRoom) => (
                  <Box>
                    <Typography variant="body2" component="div">
                      {option.name}
                    </Typography>
                    <Typography variant="caption">
                      {option.floor_number}階 / {option.m2}m²
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <CustomAutoCompleteForm
                    params={params}
                    label="部屋"
                    helperText="部屋を選ぶと、号室/所在階/専有面積/間取りを自動入力します"
                    ref={params.InputProps.ref}
                  />
                )}
                onChange={(e, selected: HowMaRoom | null) => {
                  setSelectedHowMaRoom(selected);
                }}
              />
            )}

            {/* 号室・所在階 */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  control={control}
                  name="room_number"
                  defaultValue={formData?.room_number || ""}
                  render={({ ...field }) => (
                    <CustomForm
                      {...field}
                      label="号室"
                      required
                      error={!!errors.room_number}
                      errorText={errors.room_number?.message}
                      onChange={field.onChange}
                      placeHolder="102"
                      unitSuffix="号室"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="floor"
                  control={control}
                  defaultValue={formData.floor || ""}
                  render={({ ...field }) => (
                    <>
                      <CustomForm
                        {...field}
                        label="所在階"
                        required
                        error={!!errors.floor}
                        errorText={errors.floor?.message}
                        onChange={field.onChange}
                        placeHolder="1"
                        unitSuffix="階"
                      />
                      <Typography variant="caption">※一棟の場合は「0」と入力</Typography>
                    </>
                  )}
                />
              </Grid>
            </Grid>

            {/* 都道府県 */}
            <RegistrationSection title="住所" />
            <Controller
              control={control}
              name="prefecture"
              defaultValue={formData.prefecture || ""}
              render={({ ...field }) => (
                <RegistrationAutocomplete
                  registrationLabel="都道府県"
                  registrationOnBlur={field.onBlur}
                  registrationError={errors.prefecture?.message}
                  registrationRequired={true}
                  id="prefecture"
                  options={!field.value || field.value === "" ? [field.value, ...prefectures] : prefectures}
                  value={field.value || ""}
                  size="small"
                  filterOptions={(options, state) => {
                    const defaultFiltered = defaultFilterOptions(options, state);
                    return defaultFiltered.filter(
                      (option): option is string => option !== "" && option !== null
                    ) as string[];
                  }}
                  getOptionLabel={(option) => (!option ? "" : option)}
                  renderOption={(params) => <>{params}</>}
                  renderInput={(params) => (
                    <CustomAutoCompleteForm
                      params={params}
                      label="都道府県"
                      required
                      ref={field.ref}
                      error={!!errors.prefecture}
                      errorText={errors.prefecture?.message}
                    />
                  )}
                  onChange={(e, data) => {
                    field.onChange(data);
                    setValue("city", "");
                    setValue("town", "");
                  }}
                />
              )}
            />

            {/* 市区町村 */}
            <Controller
              name="city"
              control={control}
              defaultValue={formData.city || ""}
              render={({ ...field }) => (
                <RegistrationAutocomplete
                  registrationLabel="市区町村"
                  registrationOnBlur={field.onBlur}
                  registrationError={errors.city?.message}
                  registrationRequired={true}
                  id="city"
                  options={!field.value || field.value === "" ? [field.value, ...cities] : cities}
                  value={field.value}
                  size="small"
                  filterOptions={(options, state) => {
                    const defaultFiltered = defaultFilterOptions(options, state);
                    return defaultFiltered.filter(
                      (option): option is string => option !== "" && option !== null
                    ) as string[];
                  }}
                  getOptionLabel={(option) => (!option ? "" : option)}
                  getOptionSelected={(option, value) => option === value}
                  renderOption={(params) => <>{params}</>}
                  renderInput={(params) => (
                    <CustomAutoCompleteForm
                      params={params}
                      label="市区町村"
                      required
                      ref={field.ref}
                      error={!!errors.city}
                      errorText={errors.city?.message}
                    />
                  )}
                  onChange={(e, data) => {
                    field.onChange(data);
                    setValue("town", "");
                  }}
                  disabled={!watchValues.prefecture || watchValues.prefecture === ""}
                />
              )}
            />

            {/* 番地 */}
            <Controller
              control={control}
              name="town"
              error={!!errors.town}
              helperText={errors.town?.message}
              defaultValue={formData.town || ""}
              render={({ ...field }) => (
                <RegistrationAutocomplete
                  registrationLabel="番地"
                  registrationOnBlur={field.onBlur}
                  registrationError={errors.town?.message}
                  registrationRequired={true}
                  id="town"
                  options={
                    !field.value || field.value === ""
                      ? [field.value, ...towns]
                      : towns.includes(field.value)
                      ? towns
                      : [field.value, ...towns]
                  }
                  value={field.value}
                  size="small"
                  filterOptions={(options, state) => {
                    const defaultFiltered = defaultFilterOptions(options, state);
                    return defaultFiltered.filter(
                      (option): option is string => option !== "" && option !== null
                    ) as string[];
                  }}
                  getOptionLabel={(option) => (!option ? "" : option)}
                  renderOption={(params) => <>{params}</>}
                  renderInput={(params) => (
                    <CustomAutoCompleteForm
                      params={params}
                      label="番地"
                      ref={field.ref}
                      error={!!errors.town}
                      errorText={errors.town?.message}
                    />
                  )}
                  onChange={(e, data) => field.onChange(data)}
                  disabled={!watchValues.city || watchValues.city === ""}
                />
              )}
            />

            {/* 路線名 */}
            <Controller
              name="railway"
              control={control}
              defaultValue={formData.railway || ""}
              render={({ ...field }) => (
                <RegistrationAutocomplete
                  registrationLabel="路線名"
                  registrationOnBlur={field.onBlur}
                  registrationError={errors.railway?.message}
                  registrationRequired={true}
                  id="railway"
                  options={railways}
                  value={railways.find((r) => r.name === field.value) || null}
                  size="small"
                  getOptionLabel={(option) => (option ? option.name : "")}
                  renderOption={(option: IdName) => <>{option.name}</>}
                  renderInput={(params) => (
                    <CustomAutoCompleteForm
                      params={params}
                      label="路線名"
                      required
                      ref={params.InputProps.ref}
                      error={!!errors.railway}
                      errorText={errors.railway?.message}
                    />
                  )}
                  onChange={(e, data: IdName | null) => {
                    field.onChange(data?.name || "");
                    setValue("station", "");
                  }}
                />
              )}
            />

            {/* 駅名 */}
            <Controller
              name="station"
              control={control}
              defaultValue={formData.station || ""}
              render={({ ...field }) => (
                <RegistrationAutocomplete
                  registrationLabel="駅名"
                  registrationOnBlur={field.onBlur}
                  registrationError={errors.station?.message}
                  registrationRequired={true}
                  id="station"
                  options={!field.value || field.value === "" ? [field.value, ...stations] : stations}
                  value={field.value || ""}
                  size="small"
                  filterOptions={(options, state) => {
                    const defaultFiltered = defaultFilterOptions(options, state);
                    return defaultFiltered.filter(
                      (option): option is string => option !== "" && option !== null
                    ) as string[];
                  }}
                  getOptionLabel={(option) => (!option ? "" : option)}
                  renderOption={(params) => <>{params}</>}
                  renderInput={(params) => (
                    <CustomAutoCompleteForm
                      params={params}
                      label="駅名"
                      required
                      ref={field.ref}
                      error={!!errors.station}
                      errorText={errors.station?.message}
                    />
                  )}
                  onChange={(e, data) => field.onChange(data || "")}
                  disabled={!watchValues.railway || watchValues.railway === ""}
                />
              )}
            />

            {/* 駅徒歩 */}
            <Controller
              name="walk"
              control={control}
              defaultValue={formData.walk || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="駅徒歩"
                  required
                  error={!!errors.walk}
                  errorText={errors.walk?.message}
                  onChange={field.onChange}
                  placeHolder="1"
                  unitSuffix="分"
                  isShort
                />
              )}
            />

            {/* 築年 */}
            <RegistrationSection title="建物の情報" />
            <Controller
              name="built_at"
              control={control}
              defaultValue={formData.built_at ? dayjs(formData.built_at).format("YYYY") : dayjs().format("YYYY")}
              render={({ ...field }) => (
                <CustomSelector
                  {...field}
                  label="築年"
                  required
                  error={!!errors.built_at}
                  errorText={errors.built_at?.message}
                  onChange={field.onChange}
                  options={builtAtYearOptions}
                  placeHolder={`${currentYear}`}
                  suffix="年"
                />
              )}
            />

            {/* 専有面積 */}
            <Controller
              name="occupied_area_m2"
              control={control}
              defaultValue={formData.occupied_area_m2 || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="専有面積"
                  required
                  error={!!errors.occupied_area_m2}
                  errorText={errors.occupied_area_m2?.message}
                  onChange={field.onChange}
                  placeHolder="32"
                  unitSuffix="m²"
                  isShort
                />
              )}
            />

            {/* 間取り */}
            <Controller
              name="layout"
              control={control}
              defaultValue={formData.layout || ""}
              render={({ ...field }) => (
                <CustomSelector
                  {...field}
                  label="間取り"
                  onChange={field.onChange}
                  error={!!errors.layout}
                  errorText={errors.layout?.message}
                  options={Layout.map((layout) => ({ label: layout, value: layout }))}
                  placeHolder="未選択"
                />
              )}
            />

            {/* 構造 */}
            <Controller
              name="structure"
              control={control}
              defaultValue={formData.structure || PropertyStructureEnum.Rc造鉄筋コンクリート造}
              render={({ ...field }) => (
                <CustomSelector
                  {...field}
                  label="構造"
                  onChange={field.onChange}
                  error={!!errors.structure}
                  errorText={errors.structure?.message}
                  options={[
                    { label: "SRC造(鉄骨鉄筋コンクリート造)", value: "SRC造(鉄骨鉄筋コンクリート造)" },
                    { label: "RC造(鉄筋コンクリート造)", value: "RC造(鉄筋コンクリート造)" },
                    { label: "S造(重量鉄骨造)", value: "S造(重量鉄骨造)" },
                    { label: "木造", value: "木造" },
                  ]}
                />
              )}
            />
          </>

          {/* AIおまかせ入力 */}
          {registrationMode !== "manual" && (
            <RegistrationActions>
              <Typography className={classes.aiDescription}>
                AIが推定した内容を確認・修正してから登録できます。
              </Typography>

              {/* AIおまかせ入力ボタン */}
              <Box mt={2}>
                <CustomButton customVariant="filled" onClick={handleAiCreate} disabled={isAiCreating}>
                  {isAiCreating ? "入力中..." : "AIで入力する"}
                </CustomButton>
              </Box>
            </RegistrationActions>
          )}
          {registrationMode !== "ai" && (
            <RegistrationActions>
              <Box mt={2}>
                <CustomButton type="button" customVariant="filled" onClick={handleNext}>
                  次へ
                </CustomButton>
              </Box>
            </RegistrationActions>
          )}
        </Box>
      </Box>

      {/* AI入力中オーバーレイ */}
      {isAiCreating && (
        <Box className={classes.loadingOverlay}>
          <Box
            role="status"
            aria-live="polite"
            className={classes.loadingCard}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <img src="/assets/image/ds_v2_registration_spinner.svg" alt="" className={classes.spinner} />
            <Box mt={2}>
              <Typography className={classes.aiLoadingTitle}>AIが物件を調べています</Typography>
              <Typography className={classes.aiLoadingDescription}>
                相場・利回り・経費の目安を集めています。最大60秒ほどかかります。
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* トースト通知 */}
      <Toast open={!!serverError} variant="error" message={serverError} onClose={handleClose} />
      <Toast open={!!successMessage} variant="success" message={successMessage} onClose={handleCloseSuccess} />
    </>
  );
};

export default AddNewConsiderationPropertyBasicPage;
